import * as React from "react";
import { Query, QueryCondition, isModelWithError, ModelWithError, ModelError } from "../../misc/api/apiTypes";
import Modal from "../../misc/components/modal";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { AnalysisSetReferenceTag } from "../Shared/AnalysisSetReferenceTag";
import { useWizard } from "../../DataCorrectionFacility/Hooks/UseWizard";
import { AuditModalBody } from "../../auditmodal/components/AuditModalBody";
import { WizardActionTypes } from "../../DataCorrectionFacility/Execution/CorrectionTypes/WizardTypings";
import { AuditForm } from "../../auditmodal/components/AuditModal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import SuccessWithMessage from "../../misc/components/SuccessWithMessage";
import { WizardModal, WizardModalContent } from "../../misc/components/WizardModal";
import { FormatTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import { CoughAnalysisSetTypeEnum } from "../../misc/api/CoughAnalysisSetTypes";
import CoughAnalysisSetUtilities from "../Shared/CoughAnalysisSetUtilities";
import { CoughAnalysisSet } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

interface ChangeCreatedFromModalProps {
    analysisSetId: string;
    isVisible: boolean;
    onClose: () => void;
    currentUsername: string;
}

const ChangeCreatedFromModal = ({ analysisSetId, isVisible, onClose }: ChangeCreatedFromModalProps): JSX.Element => {

    const [loading, setLoading] = React.useState<boolean>(false);
    const [anSetsForSession, setAnalysisSetsForSession] = React.useState<Array<CoughAnalysisSet>>([]);
    const [err, setErr] = React.useState<string>();
    const [selectedAnalysisSet, setSelectedAnalysisSet] = React.useState<string>();
    const [analysisSet, setAnalysisSet] = React.useState<CoughAnalysisSet>();
    const webPortalClient = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    const header = "Change QA Analysis Set Created From Link";

    const { currentState, dispatch } = useWizard();

    const handleErr = (e: ModelError) => {
        setErr(e.Message);
    };

    const startLoading = () => {
        setLoading(true);
        setErr(null);
    };

    //If the visibility or analysis set changes, the selected analysis set should be reset
    //to account for the next time it is launched from another analysis set.
    React.useEffect(() => {

        setSelectedAnalysisSet(null);
        if (!isVisible || !analysisSetId) {
            return;
        }

        startLoading();
        webPortalClient.client.getCoughAnalysisSet(analysisSetId)
            .then(anSet => {
                setAnalysisSet(anSet);
                const existingLink = CoughAnalysisSetUtilities.GetCreatedFromLink(anSet);
                if (existingLink) {
                    setSelectedAnalysisSet(existingLink.targetId);
                }
                
            })
            .catch(handleErr);

    }, [isVisible, analysisSetId])

    //If the session changes, we should re-load the analysis sets for that session.
    React.useEffect(() => {
        if (!isVisible || !analysisSet || !analysisSet.sessionId) {
            setAnalysisSetsForSession([]);
            return;
        }

        startLoading();
        webPortalClient.client.getCoughAnalysisSets(undefined, `sessionId eq ${analysisSet.sessionId}`)
        .then(q => {
            setAnalysisSetsForSession(q.value.filter(anSet => !anSet.isLinkedAnalysisSet && anSet.id !== analysisSetId));
        }).catch(handleErr).finally(() => setLoading(false));

    }, [analysisSet, isVisible]);

    const handleClose = () => {
        setLoading(false);
        setErr(null);
        setSelectedAnalysisSet(null);
        dispatch(WizardActionTypes.Reset);
        onClose();
    }

    const renderFinished = () => {

        return (<WizardModal
            error={err}
            header={header}
            isVisible={isVisible}
            onClose={handleClose}
            
        >
            <WizardModalContent
                disabled={false}
                nextText="Finish"
                onNext={handleClose}
            >
                <SuccessWithMessage message="The Analysis Set Was Changed Successfully"></SuccessWithMessage>
            </WizardModalContent>
        </WizardModal>);
    };

    const handleSubmit = (form: AuditForm) => {
        const annotation = form.Annotation || "";

        startLoading();

        legacyClient.updateCreatedFromAnalysisSet(analysisSetId, selectedAnalysisSet, annotation.trim())
            .then(() => dispatch(WizardActionTypes.Next))
            .catch(handleErr)
            .finally(() => setLoading(false));
    }

    const renderSelect = () => {
        const currentLink = CoughAnalysisSetUtilities.GetCreatedFromLink(analysisSet);

        const currentLinkId = currentLink && currentLink.targetId;

        return (<WizardModal
            error={err}
            header={header}
            isVisible={isVisible}
            onClose={handleClose}
        >
            <WizardModalContent
                disabled={!selectedAnalysisSet || selectedAnalysisSet === analysisSetId || selectedAnalysisSet === currentLinkId}
                nextText="Next"
                onNext={() => dispatch(WizardActionTypes.Next)}
                backText="Close"
                onBack={handleClose}
            >
                <select
                    className="form-control"
                    value={selectedAnalysisSet || ""}
                    onChange={e => setSelectedAnalysisSet(e.target.value)}
                >
                    {anSetsForSession.map(i => (
                        <option key={i.id} value={i.id}>
                            {i.typeName} - {FormatTime(i.created, DateTimeAccuracy.Minute, false)} ({i.statusName})
                        </option>
                    ))}
                </select>
            </WizardModalContent>
        </WizardModal>);
    };

    const renderAuditModalBody = () => {

        return (<WizardModal
            error={err}
            header={header}
            isVisible={isVisible}
            onClose={handleClose}
        >
            <AuditModalBody
                isSubmitting={loading}
                onClose={handleClose}
                onSubmit={handleSubmit}
            />
        </WizardModal>);
    }

    const renderPage = () => {
        switch (currentState.page) {
            case 0:
                return renderSelect();
            case 1:
                return renderAuditModalBody();
            case 2:
                return renderFinished();
            default:
                return null;
        }
    };
     
    const renderBody = () => {

        return (loading || !analysisSet ? <div className="modal-body">
            <LoadingWithMessage message="Loading..." />
        </div> : renderPage());
    };

    return (<Modal
        show={isVisible}
        closeModalHandler={onClose}
        header={header}>
        {renderBody()}
    </Modal>);
};

export default ChangeCreatedFromModal;
